<template>
  <div class="d-flex justify-content-center align-items-center mt-5">
    <LoadingSpinner v-if="loading || sessionLoading" :text="'...Anmeldedaten werden geprüft...'" />

    <div v-else class="d-flex-col">
      <div class="mb-5 text-center">
        <!-- <font-awesome-icon icon="users-line" class="me-3 mb-2 fa-3x" /> -->
        <img src="@/assets/icons/archiv.png" alt="Archiv Icon" width="64" />
        <h1>Archiv</h1>
      </div>

      <section v-if="loginConfig != null && loginConfig.userLoginEnabled && displayBenutzerLogin">
        <form class="form-signin p-0" @submit.prevent="login">
          <div class="form-floating mb-3">
            <input type="text" class="form-control" id="floatingInput" placeholder="Anmeldename" v-model="user" required />
            <label for="floatingInput">Benutzername</label>
          </div>

          <div class="form-floating mb-3">
            <input type="password" class="form-control" id="floatingPassword" placeholder="Password" v-model="passwd" required />
            <label for="floatingPassword">Passwort</label>
          </div>

          <button type="submit" class="w-100 btn btn-primary text-light">
            <font-awesome-icon icon="arrow-right-to-bracket" class="me-3" />
            Anmelden
          </button>
        </form>
      </section>

      <div v-if="errorMsg != '' && errorMsg != 'Code ungültig.'" class="alert alert-danger">
        {{ errorMsg }}
      </div>

      <section v-if="loginConfig != null && loginConfig.userLoginEnabled && displayCheckOTPLogin">
        <form class="d-flex-col justify-content-center" @submit.prevent="checkOTPlogin">
          <p>Geben Sie den Code ein, den Sie per E-Mail bekommen haben.</p>

          <div class="form-floating mb-2 w-75 mx-auto">
            <input type="text" class="form-control" id="floatingOTPInput" placeholder="XXXXXX" v-model="otpcode" required />
            <label for="floatingOTPInput">Code eingeben</label>
          </div>

          <div class="form-check form-switch mx-auto mb-4 text-body-tertiary">
            <input class="form-check-input" type="checkbox" role="switch" id="secureBrowserSwitchCheck" v-model="secureBrowser" />
            <label class="form-check-label" for="secureBrowserSwitchCheck small">Auf diesem Gerät nicht mehr nach dem Code fragen</label>
          </div>

          <div v-if="errorMsg == 'Code ungültig.'" class="alert alert-danger">
            {{ errorMsg }}
          </div>

          <div class="w-75 mx-auto">
            <button type="submit" class="btn btn-lg btn-primary text-light w-100">
              <font-awesome-icon icon="arrow-right-to-bracket" class="me-3" />
              Senden
            </button>
          </div>
        </form>
      </section>

      <hr class="my-4" v-if="loginConfig?.userLoginEnabled && (keycloakLoginAvailable || azureLoginAvailable)" />

      <div v-if="loginConfig != null && loginConfig.keycloakLoginEnabled && keycloakLoginAvailable" class="mt-2">
        <button class="btn btn-outline-success w-100" @click="signInKeycloak">
          <font-awesome-icon icon="arrow-right-to-bracket" class="me-3" />
          SSO Login
        </button>
      </div>

      <div v-if="loginConfig != null && loginConfig.azureLoginEnabled && azureLoginAvailable" class="mt-2">
        <button class="btn btn-outline-primary w-100" @click="signInMicrosoft">
          <font-awesome-icon :icon="['fab', 'windows']" class="me-3" />
          Weiter mit Microsoft
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  //#region Imports

  import { computed, onMounted, ref } from 'vue';
  import { useStore } from '@/store';
  import { v4 as uuidv4 } from 'uuid';
  import { AuthenticationResult } from '@azure/msal-browser';
  import { PublicClientApplication, type Configuration, type PopupRequest, type RedirectRequest } from '@azure/msal-browser';
  import Keycloak, { KeycloakConfig } from 'keycloak-js';

  import { LoginRequest, ExternalLoginRequest } from '@/models/AuthModels';

  import { ActionTypes as AuthActionTypes } from '@/store/modules/Auth/actions';
  import { ActionTypes as SessionActionTypes } from '@/store/modules/Session/actions';

  import LoadingSpinner from '@/components/LoadingSpinner.vue';
  //#endregion

  const store = useStore();

  const loginConfig = computed(() => store.getters.loginConfig);

  const user = ref('');
  const passwd = ref('');
  const otpcode = ref('');
  const secureBrowser = ref(false);

  const loading = computed(() => store.getters.status.authLoading);
  const sessionLoading = computed(() => store.getters.status.sessionLoading);
  const errorMsg = computed(() => store.getters.status.authErrorMsg);

  //#region Userlogin
  const displayBenutzerLogin = ref(true);
  const displayCheckOTPLogin = ref(false);

  function login() {
    var loginRequest: LoginRequest = {
      username: user.value,
      userpassword: passwd.value,
      secureBrowserToken: '',
    };

    if (localStorage.getItem('SECBROWSTOKEN') != null && localStorage.getItem('SECBROWSTOKEN') !== undefined) {
      const token = localStorage.SECBROWSTOKEN;
      loginRequest.secureBrowserToken = token;
    }

    store.dispatch(AuthActionTypes.Login, loginRequest).then(() => {
      if (errorMsg.value == 'OTP required.') {
        localStorage.removeItem('SECBROWSTOKEN');
        displayBenutzerLogin.value = false;
        displayCheckOTPLogin.value = true;
      } else if (errorMsg.value == '') {
        store.dispatch(SessionActionTypes.GetSessions, undefined);
      }
    });
  }

  function checkOTPlogin() {
    var loginRequest: LoginRequest = {
      username: user.value,
      userpassword: otpcode.value,
      secureBrowserToken: '',
    };

    if (secureBrowser.value) {
      const token = uuidv4();
      loginRequest.secureBrowserToken = token;
      localStorage.setItem('SECBROWSTOKEN', token);
    }

    store.dispatch(AuthActionTypes.ValidateOTP, loginRequest).then(() => {
      if (errorMsg.value == 'Code ungültig.') {
        displayBenutzerLogin.value = true;
        displayCheckOTPLogin.value = false;
      } else if (errorMsg.value == '') {
        store.dispatch(SessionActionTypes.GetSessions, undefined);
      }
    });
  }
  //#endregion

  //#region Azure
  const azureLoginAvailable = computed(() => {
    if (
      loginConfig.value != null &&
      loginConfig.value.azureLoginEnabled &&
      loginConfig.value.azureClientId != null &&
      loginConfig.value.azureClientId.trim() != '' &&
      loginConfig.value.azureAuthority != null &&
      loginConfig.value.azureAuthority.trim() != ''
    ) {
      return true;
    }

    return false;
  });

  async function signInMicrosoft() {
    const msalConfig = store.getters.getAzureMsalConfig as Configuration;

    if (msalConfig == undefined || !azureLoginAvailable.value) {
      return;
    }

    const msalInstance = new PublicClientApplication(msalConfig);
    await msalInstance.initialize();

    try {
      // TEST ob Popup-Flow mit https funktioniert
      if (window.location.origin.startsWith('https')) {
        const loginRequest: PopupRequest = {
          scopes: ['openid', 'profile', 'email'],
          prompt: 'select_account',
          redirectUri: window.location.origin + '/azure-redirect.html',
        };

        const response = await msalInstance.loginPopup(loginRequest);
        handleAzureResponse(response);
      } else {
        const loginRequest: RedirectRequest = {
          scopes: ['openid', 'profile', 'email'],
          prompt: 'select_account',
        };

        msalInstance.loginRedirect(loginRequest);
      }
    } catch (error) {
      console.error('Azure Login Error:', error);
    }
  }

  function handleAzureResponse(response: AuthenticationResult) {
    if (response != null) {
      let identyToken = response.idToken;
      let username = response.account?.username != undefined ? response.account?.username : '';

      const azureLogin: ExternalLoginRequest = new ExternalLoginRequest(identyToken, username);
      store.dispatch(AuthActionTypes.LoginAzure, azureLogin).then(() => {
        store.dispatch(SessionActionTypes.GetSessions, undefined);
      });
    }
  }
  //#endregion

  //#region Keycloak
  const keycloakLoginAvailable = computed(() => {
    if (
      loginConfig.value != null &&
      loginConfig.value.keycloakLoginEnabled &&
      loginConfig.value.keycloakClientId != null &&
      loginConfig.value.keycloakClientId.trim() != '' &&
      loginConfig.value.keycloakAuthority != null &&
      loginConfig.value.keycloakAuthority.trim() != '' &&
      loginConfig.value.keycloakRealm != null &&
      loginConfig.value.keycloakRealm.trim() != ''
    ) {
      return true;
    }

    return false;
  });

  async function signInKeycloak() {
    const keycloakConfig = store.getters.getKeycloakConfig as KeycloakConfig;

    if (keycloakConfig == null || !keycloakLoginAvailable.value) {
      return;
    }

    try {
      const keycloak = new Keycloak(keycloakConfig);

      // Initialisiere Keycloak und prüfe auf bestehende Session
      const authenticated = await keycloak.init({
        // onLoad: 'check-sso',
        onLoad: 'login-required',
        // silentCheckSsoRedirectUri: window.location.origin + '/login',
        redirectUri: window.location.origin + '/login',
      });

      console.log('Keycloak authenticated:', authenticated);

      if (authenticated && keycloak.token) {
        // Bereits authentifiziert
        handleKeycloakResponse(keycloak);
      } else {
        // Login durchführen
        await keycloak.login({
          redirectUri: window.location.origin + '/login',
        });
      }
    } catch (error) {
      console.error('Keycloak Login Error:', error);
    }
  }

  function handleKeycloakResponse(keycloak: Keycloak) {
    if (keycloak.idToken && keycloak.idTokenParsed) {
      const identityToken = keycloak.idToken;
      const username = keycloak.idTokenParsed.preferred_username || keycloak.idTokenParsed.email || '';

      const loginData: ExternalLoginRequest = new ExternalLoginRequest(identityToken, username);
      store.dispatch(AuthActionTypes.LoginKeycloak, loginData).then(() => {
        store.dispatch(SessionActionTypes.GetSessions, undefined);
      });
    }
  }
  //#endregion

  onMounted(async () => {
    if (store.getters.loginConfig == undefined) {
      await store.dispatch(AuthActionTypes.GetLoginConfig, undefined);
    }

    // handleRedirectPromise für Azure
    const msalConfig = store.getters.getAzureMsalConfig;
    if (msalConfig && azureLoginAvailable.value) {
      try {
        const msalInstance = new PublicClientApplication(msalConfig);
        await msalInstance.initialize();
        const response = await msalInstance.handleRedirectPromise();

        if (response && response.account) {
          handleAzureResponse(response);
        }
      } catch (error) {
        console.error('Login: Error handling Azure redirect:', error);
      }
    }

    // Keycloak-Session prüfen
    const keycloakConfig = store.getters.getKeycloakConfig;
    if (keycloakConfig && keycloakLoginAvailable.value) {
      try {
        const keycloak = new Keycloak(keycloakConfig);
        const authenticated = await keycloak.init();

        if (authenticated && keycloak.idToken) {
          handleKeycloakResponse(keycloak);
        }
      } catch (error) {
        console.error('Login: Error handling Keycloak redirect:', error);
      }
    }
  });
</script>
